"use client";

import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import Image from "next/image";
import { useWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";

/**
 * Optional dismissible promo overlay from Figma (`4365-2767`).
 * Shows once per browser until dismissed in localStorage, and never after the
 * org has recorded at least one API usage event (all-time ledger check).
 * Copy amount is the current unused credit balance (welcome and/or admin grants).
 */
export const DashboardPromoModal = () => {
  const { promoAmountCents, shouldShowPromo, reveal } = useWelcomeCreditReveal();
  const { data: billingData } = useBillingOverview();

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const realMicro = Number(billingData?.billing_overview?.credit_balance_micro ?? 0);
      reveal(Number.isFinite(realMicro) ? realMicro : 0);
    }
  };

  const amountLabel = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(promoAmountCents / 100);

  return (
    <Dialog open={shouldShowPromo} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton
        className="max-w-sm gap-6 border-none bg-zinc-900 p-8 pb-12 text-zinc-50 sm:rounded-none **:data-[slot=dialog-close]:text-zinc-50 **:data-[slot=dialog-close]:opacity-80"
      >
        <Image
          src="/marketing/mkt-presents.svg"
          alt="Gift boxes"
          width={184}
          height={106}
          className="mx-auto h-24 w-auto"
          priority
        />
        <DialogHeader className="gap-2 text-center sm:text-center">
          <DialogTitle className="text-3xl font-semibold tracking-tight text-zinc-50">
            Your first {amountLabel} is on us.
          </DialogTitle>
          <DialogDescription className="sr-only">
            Promotional credit offer. Dismiss or continue to get started.
          </DialogDescription>
        </DialogHeader>
        <Button
          type="button"
          className="mx-auto w-full rounded-full bg-white text-zinc-900 hover:bg-zinc-100 sm:w-auto"
          onClick={() => handleOpenChange(false)}
        >
          Get started
        </Button>
      </DialogContent>
    </Dialog>
  );
};
